import { useAppDispatch, useAppSelector } from 'app/store/store';
import {
  hideWeather,
  setPressureVisibility,
  setTempVisibility,
  setWavesVisibility,
  setWindVisibility,
} from 'app/utils/weatherReducer';
import styles from 'app/components/MapSettings/MapSetting.module.scss';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { FloatingComponent } from '../FloatingComponent';
import { Input } from '../Input';

export const WeatherVisibility = () => {
  const dispatch = useAppDispatch();
  const isTemperatureVisible = useAppSelector((state) => state.weatherSlice.layersVisibility.temperature);
  const isWavesVisible = useAppSelector((state) => state.weatherSlice.layersVisibility.waves);
  const isPressureVisible = useAppSelector((state) => state.weatherSlice.layersVisibility.pressure);
  const isWindBarbVisible = useAppSelector((state) => state.weatherSlice.layersVisibility.wind.barb);
  const isWindParticlesVisible = useAppSelector((state) => state.weatherSlice.layersVisibility.wind.particles);

  const uiConfig = useAppSelector((state) => state.userSlice.uiConfig);

  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isWeatherHiddenMode = mode === 'history' || mode === 'playback' || mode === 'analytics';

  enum Weather {
    WindBarb = 'windBarb',
    WindParticles = 'windParticles',
    Temperature = 'temperature',
    Hide = 'hide',
    Waves = 'waves',
    Pressure = 'pressure',
  }

  const defaultValue = isWindBarbVisible
    ? Weather.WindBarb
    : isWindParticlesVisible
      ? Weather.WindParticles
      : isTemperatureVisible
        ? Weather.Temperature
        : isWavesVisible
          ? Weather.Waves
          : isPressureVisible
            ? Weather.Pressure
            : Weather.Hide;

  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    if (!isWeatherHiddenMode) {
      switch (value) {
        case Weather.WindBarb:
          dispatch(setTempVisibility(false));
          dispatch(setWavesVisibility(false));
          dispatch(setPressureVisibility(false));
          dispatch(
            setWindVisibility({
              barb: true,
              particles: false,
            }),
          );
          break;
        case Weather.WindParticles:
          dispatch(setTempVisibility(false));
          dispatch(setWavesVisibility(false));
          dispatch(setPressureVisibility(false));
          dispatch(
            setWindVisibility({
              barb: false,
              particles: true,
            }),
          );
          break;
        case Weather.Temperature:
          dispatch(
            setWindVisibility({
              barb: false,
              particles: false,
            }),
          );
          dispatch(setWavesVisibility(false));
          dispatch(setPressureVisibility(false));
          dispatch(setTempVisibility(true));
          break;
        case Weather.Waves:
          dispatch(
            setWindVisibility({
              barb: false,
              particles: false,
            }),
          );
          dispatch(setTempVisibility(false));
          dispatch(setPressureVisibility(false));
          dispatch(setWavesVisibility(true));
          break;
        case Weather.Pressure:
          dispatch(
            setWindVisibility({
              barb: false,
              particles: false,
            }),
          );
          dispatch(setTempVisibility(false));
          dispatch(setWavesVisibility(false));
          dispatch(setPressureVisibility(true));
          break;
        case Weather.Hide:
          dispatch(hideWeather());
          break;
        default:
          dispatch(hideWeather());
          break;
      }
    }
  }, [value, mode]);

  useEffect(() => {
    if (!uiConfig?.feWeatherAvailable || isWeatherHiddenMode) {
      dispatch(hideWeather());
    }
  }, [uiConfig?.feWeatherAvailable, mode]);

  return (
    <FloatingComponent>
      <div className={styles.radio_group}>
        <Input
          label={t('Temperature')}
          name="weatherVisibility"
          type="radio"
          value={Weather.Temperature}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={isTemperatureVisible}
        />
        <Input
          label={t('WindBarbs')}
          name="weatherVisibility"
          type="radio"
          value={Weather.WindBarb}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={isWindBarbVisible}
        />
        <Input
          label={t('Wind')}
          name="weatherVisibility"
          type="radio"
          value={Weather.WindParticles}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={isWindParticlesVisible}
        />
        <Input
          label={t('Waves')}
          name="weatherVisibility"
          type="radio"
          value={Weather.Waves}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={isWavesVisible}
        />
        <Input
          label={t('Pressure')}
          name="weatherVisibility"
          type="radio"
          value={Weather.Pressure}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={isPressureVisible}
        />
        <Input
          label={t('HideWeather')}
          name="weatherVisibility"
          type="radio"
          value={Weather.Hide}
          onChange={(e) => {
            setValue(e.target.value);
          }}
          checked={!isWindBarbVisible && !isWindParticlesVisible && !isTemperatureVisible && !isWavesVisible && !isPressureVisible}
        />
      </div>
    </FloatingComponent>
  );
};
